import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Truck, Package, MapPin, Thermometer, Clock, 
  CheckCircle, AlertTriangle, XCircle, QrCode,
  Download, Shield, Weight, DoorOpen, DoorClosed
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const statusColors = {
  delivered: 'bg-green-500',
  in_transit: 'bg-blue-500',
  pending: 'bg-slate-500',
  failed: 'bg-red-500',
};

const eventIcons = {
  trip_start: Truck,
  trip_end: CheckCircle,
  bag_open: DoorOpen,
  bag_close: DoorClosed,
  weight_delta: Weight,
  invoice_delivery: Package,
  qr_scan: QrCode,
  temp_breach: AlertTriangle,
};

export default function DeliveryVerificationPage() {
  const { qrCode } = useParams();
  const [searchParams] = useSearchParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    if (qrCode) {
      fetchTripInfo();
    }
  }, [qrCode]);

  const fetchTripInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/v1/delivery/verify/${qrCode}`);
      setTrip(response.data);
    } catch (error) {
      console.error('Error fetching trip:', error);
      toast.error('Invalid QR code or trip not found');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyInvoice = async () => {
    if (!invoiceNumber.trim()) {
      toast.error('Please enter invoice number');
      return;
    }

    try {
      setVerifying(true);
      const response = await axios.post(`${API}/api/v1/delivery/verify/${qrCode}/invoice`, {
        invoice_number: invoiceNumber.trim(),
        receiver_name: receiverName.trim() || undefined
      });
      setVerificationResult(response.data);
      toast.success('Delivery verified successfully!');
      // Refresh trip info
      fetchTripInfo();
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading trip information...</p>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Invalid QR Code</h2>
            <p className="text-slate-400">
              The QR code is invalid or the trip does not exist.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="delivery-verification-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center py-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-8 h-8 text-teal-500" />
            <h1 className="text-2xl font-bold text-white">
              Track<span className="text-red-500">0</span>tic
            </h1>
          </div>
          <p className="text-slate-400">Cold Chain Delivery Verification</p>
        </div>

        {/* Trip Info Card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-white">
              <Truck className="w-5 h-5 text-teal-500" />
              Trip: {trip.trip_number}
            </CardTitle>
            <CardDescription className="text-slate-400">
              Verify your delivery by entering the invoice number
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-400">Driver:</span>
                <p className="text-white">{trip.driver_name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-400">Vehicle:</span>
                <p className="text-white">{trip.vehicle_number || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-400">Origin:</span>
                <p className="text-white">{trip.origin_address || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-400">Temp Range:</span>
                <p className="text-white">{trip.temp_min}°C - {trip.temp_max}°C</p>
              </div>
            </div>

            {/* Invoices List */}
            <div className="border-t border-slate-700 pt-4">
              <h3 className="text-sm font-medium text-slate-400 mb-3">Invoices in this trip:</h3>
              <div className="space-y-2">
                {trip.invoices?.map((inv) => (
                  <div 
                    key={inv.trip_invoice_id}
                    className="flex items-center justify-between p-3 bg-slate-900 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Package className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-white font-medium">{inv.invoice_number}</p>
                        <p className="text-slate-400 text-sm">{inv.customer_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {inv.verified ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge className={`${statusColors[inv.status]} text-white`}>
                          {inv.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verification Form */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-teal-500" />
              Verify Your Delivery
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Invoice Number *</label>
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Enter invoice number (e.g., INV-20260307-0001)"
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="invoice-number-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Your Name (optional)</label>
              <Input
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                placeholder="Enter your name"
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="receiver-name-input"
              />
            </div>
            <Button
              onClick={handleVerifyInvoice}
              disabled={verifying || !invoiceNumber.trim()}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="verify-delivery-btn"
            >
              {verifying ? 'Verifying...' : 'Verify Delivery'}
            </Button>
          </CardContent>
        </Card>

        {/* Verification Result */}
        {verificationResult && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                {verificationResult.delivery_confirmed ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                )}
                Verification Result
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Invoice:</span>
                  <p className="text-white font-medium">{verificationResult.invoice_number}</p>
                </div>
                <div>
                  <span className="text-slate-400">Customer:</span>
                  <p className="text-white">{verificationResult.customer_name}</p>
                </div>
                <div>
                  <span className="text-slate-400">Status:</span>
                  <Badge className={verificationResult.delivery_confirmed ? 'bg-green-500' : 'bg-amber-500'}>
                    {verificationResult.delivery_confirmed ? 'Delivered' : 'Pending'}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-400">Confirmed At:</span>
                  <p className="text-white">{formatDate(verificationResult.confirmed_at)}</p>
                </div>
              </div>

              {/* Temperature Compliance */}
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                  <Thermometer className="w-4 h-4" />
                  Temperature Compliance
                </h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="bg-slate-900 p-3 rounded-lg text-center">
                    <p className="text-slate-400 text-xs">Min</p>
                    <p className="text-blue-400 font-bold">
                      {verificationResult.temp_min_observed?.toFixed(1) || 'N/A'}°C
                    </p>
                  </div>
                  <div className="bg-slate-900 p-3 rounded-lg text-center">
                    <p className="text-slate-400 text-xs">Max</p>
                    <p className="text-red-400 font-bold">
                      {verificationResult.temp_max_observed?.toFixed(1) || 'N/A'}°C
                    </p>
                  </div>
                  <div className="bg-slate-900 p-3 rounded-lg text-center">
                    <p className="text-slate-400 text-xs">Avg</p>
                    <p className="text-teal-400 font-bold">
                      {verificationResult.temp_avg_observed?.toFixed(1) || 'N/A'}°C
                    </p>
                  </div>
                  <div className="bg-slate-900 p-3 rounded-lg text-center">
                    <p className="text-slate-400 text-xs">Breaches</p>
                    <p className={`font-bold ${verificationResult.breach_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {verificationResult.breach_count}
                    </p>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <Badge 
                    className={
                      verificationResult.compliance_status === 'compliant' 
                        ? 'bg-green-500' 
                        : verificationResult.compliance_status === 'partial'
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                    }
                  >
                    {verificationResult.compliance_status?.toUpperCase() || 'PENDING'}
                  </Badge>
                </div>
              </div>

              {/* Event Timeline */}
              {verificationResult.events?.length > 0 && (
                <div className="border-t border-slate-700 pt-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Event Timeline
                  </h4>
                  <div className="space-y-2">
                    {verificationResult.events.map((event, idx) => {
                      const Icon = eventIcons[event.type] || Package;
                      return (
                        <div key={idx} className="flex items-start gap-3 p-2 bg-slate-900 rounded-lg">
                          <Icon className="w-4 h-4 text-teal-500 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-white text-sm">{event.description || event.type}</p>
                            <p className="text-slate-500 text-xs">{formatDate(event.time)}</p>
                            {event.weight_delta && (
                              <p className="text-amber-400 text-xs">Weight: {event.weight_delta} kg</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Download Report */}
              {verificationResult.report_id && (
                <Button
                  variant="outline"
                  className="w-full border-teal-600 text-teal-400 hover:bg-teal-600/20"
                  onClick={() => window.open(`${API}/api/v1/invoice-reports/${verificationResult.report_id}/pdf`, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Compliance Report
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm">
          Powered by Track<span className="text-red-500">0</span>tic | Cold Chain Compliance
        </p>
      </div>
    </div>
  );
}
